import { useState } from 'react'
import { keepPreviousData } from '@tanstack/react-query'
import { useDebounce } from 'usehooks-ts'

import Autocomplete from '@mui/material/Autocomplete'
import CircularProgress from '@mui/material/CircularProgress'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@components/_mui/Typography'

import useLoadingState from '@shared/hooks/src/useLoadingState'
import { getTestId, UserRole } from '@shared/utils'

import { hiddenOutlineInput } from '@utils/StylesHelper'
import Avatar, { AvatarPlaceholder } from '@components/Avatar'

import { useUsers } from './SelectUser.hooks'

/** Wrapper around MUI Autocomplete to find and select user */
export default function SelectUser({
  value: user,
  onChange,
  error,
  helperText,
  label,
  required,
  role,
  pinnedUser = null,
  placeholder = 'Unassigned',
  loading: outsideLoading = false,
  hover = 'none',
  hideBorder = false,
  autoFocus = false,
  ...rest
}) {
  const testId = getTestId(rest, 'user-selector')

  const [interacted, setInteracted] = useState(false)
  const [inputValue, setInputValue] = useState('')

  const search = useDebounce(inputValue.trim(), 300)

  const { data, isPending, isRefreshing } = useUsers(
    { search, role, status: 'active' },
    {
      // Increase stale time to avoid unnecessary requests
      staleTime: 60 * 1000,
      placeholderData: keepPreviousData,
      enabled: interacted,
    }
  )

  const loading = useLoadingState((interacted && (isPending || isRefreshing)) || outsideLoading)

  // Keep the pinned user (e.g. the current user) always selectable, on top, regardless of the role filter.
  const options = pinnedUser ? [pinnedUser, ...(data || []).filter((u) => u.id !== pinnedUser.id)] : data || []

  return (
    <Autocomplete
      autoComplete
      selectOnFocus
      onFocus={() => {
        if (!interacted) setInteracted(true)
      }}
      forcePopupIcon={false}
      getOptionLabel={(option) => option.fullName}
      filterOptions={(x) => x}
      value={user}
      options={options}
      loading={loading}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      onChange={(event, user) => onChange(user)}
      onInputChange={(event, search) => {
        if (!event || event.type === 'click') return setInputValue('')
        setInputValue(search)
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          autoFocus={autoFocus}
          label={label}
          required={required}
          helperText={helperText}
          error={error}
          placeholder={placeholder}
          sx={hideBorder ? hiddenOutlineInput : undefined}
          slotProps={{
            ...params.slotProps,

            input: {
              ...params.slotProps.input,
              startAdornment: user ? <Avatar size="xs" user={user} hover={hover} /> : <AvatarPlaceholder size="xs" />,
              endAdornment: (
                <>
                  {loading ? <CircularProgress size={20} /> : null}
                  {params.slotProps.input.endAdornment}
                </>
              ),
            },

            htmlInput: { ...params.slotProps.htmlInput, 'data-testid': `${testId}-input` },
          }}
        />
      )}
      renderOption={({ key, ...props }, user) => {
        const isPatient = user.role === UserRole.Patient

        return (
          <li key={user.id} {...props} data-testid={`${testId}-${user.fullName}`}>
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center', minWidth: 0 }}>
              <Avatar size="xs" user={user} />
              <Typography noWrap>
                {user.fullName}
                {isPatient ? ` #${user.id}` : ''}
                {pinnedUser && user.id === pinnedUser.id ? ' (You)' : ''}
              </Typography>
            </Stack>
          </li>
        )
      }}
      {...rest}
    />
  )
}
